import { apiSlice } from '../../apis/api';
import { CalculationStatus, SkuRedistributionResponseCollectionResponse } from '@ydistri/api-sdk';
import { CalculationsCollection } from '../../swagger/collections';
import {
  forceRefetchForInfiniteScroll,
  InfiniteScrollParams,
  OtherArgs,
  serializeFullApiBasedQueryArgs,
} from '@ydistri/ds';
import { getTags } from '../../apis/apiLib';
import { signalrClient } from '../../signalr/client';
import { SignalCalculationStatusChanged } from './calculationsTypes';
import { apiCategoryInsights } from '../../components/global/CategoryInsights/apiCategoryInsights';
import { apiCalculationStatistics } from '../CalculationDetail/Statistics/apiCalculationStatistics';
import { getSelectedProjectShortName } from '../../apis/apiHeader';

const { TAGS, TAGS_ARRAY } = getTags('calculationCategories');

export interface CalculationCategoryPairingRequest extends InfiniteScrollParams {
  calculationId: number;
  categoryId: number;
}

export interface SkuRedistributionResponseCollectionResponseExtended extends SkuRedistributionResponseCollectionResponse {
  totalCountLoading: boolean;
}

export const apiCalculationsCategories = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      /**
       * To load rows for Calculation SKU table faster, but still display total count of rows, we do the following:
       *      1. send and await a request without "inlineCount" - this resolves faster
       *      2. send (but don't await) the same request with "inlineCount"
       *      3. return data, while second request is running
       *      4. after second request is done, update totalCount in the query cache
       *
       * This way, we ensure that calculation rows are loaded faster, but we still get proper totalCount without slowing down the page.
       * We are willing to take the tradeoff of duplicated requests to make that screen faster.
       */
      getCalculationCategoryPairings: builder.query<
        SkuRedistributionResponseCollectionResponseExtended,
        CalculationCategoryPairingRequest
      >({
        queryFn: async (args, api) => {
          const query = {
            top: args.top ?? 1,
            skip: args.skip,
            inlineCount: false,
            sortings: args.sortings,
            conditions: args.conditions,
            search: args.search,
          };

          // First request to get data - without total count - awaited
          const response = await CalculationsCollection.getCalculationCategoryPairings(
            args.calculationId,
            args.categoryId,
            query,
          );

          const result = { data: { ...response.data, totalCountLoading: true } };

          // Second request to get total count - it is NOT awaited and cache is updated afterwards
          CalculationsCollection.getCalculationCategoryPairings(
            args.calculationId,
            args.categoryId,
            {
              ...query,
              inlineCount: true,
              top: 1,
              skip: 0,
            },
          ).then(d => {
            // Update the cached data with totalCount and set totalCountLoading to false
            const totalCount = d.data.totalCount ?? 0;
            api.dispatch(
              apiCalculationsCategories.util.updateQueryData(
                'getCalculationCategoryPairings',
                args,
                draft => {
                  draft.totalCount = totalCount;
                  draft.totalCountLoading = false;
                },
              ),
            );
          });

          return result;
        },
        providesTags: [{ type: TAGS.calculationCategoryPairings, id: 'LIST' }],
        serializeQueryArgs: serializeFullApiBasedQueryArgs<CalculationCategoryPairingRequest>(
          getSelectedProjectShortName,
        ),
        merge: (
          currentCache: SkuRedistributionResponseCollectionResponseExtended,
          newItems: SkuRedistributionResponseCollectionResponseExtended,
          otherArgs: OtherArgs<CalculationCategoryPairingRequest>,
        ) => {
          if (otherArgs.arg.skip === 0) {
            currentCache.data.splice(0, currentCache.data.length);
          }
          currentCache.data.push(...newItems.data);
        },
        forceRefetch: forceRefetchForInfiniteScroll<InfiniteScrollParams | undefined>(),
        async onCacheEntryAdded(request, api) {
          await api.cacheDataLoaded;
          const listener = (signal: SignalCalculationStatusChanged) => {
            if (signal.status === CalculationStatus.Completed) {
              //When CalculationStatus changes to Completed, several things need to be refreshed
              //invalidate the redistribution table
              api.dispatch(
                apiCalculationsCategories.util.invalidateTags([
                  { type: TAGS.calculationCategoryPairings, id: 'LIST' },
                ]),
              );
              //invalidate the calculation insights
              api.dispatch(
                apiCategoryInsights.util.invalidateTags(getTags('categoryInsights').TAGS_ARRAY),
              );
              //invalidate the calculation statistics
              api.dispatch(
                apiCalculationStatistics.util.invalidateTags(
                  getTags('calculationStatistics2').TAGS_ARRAY,
                ),
              );
            }
          };

          signalrClient.on('CalculationStatusChanged', listener);

          api.cacheEntryRemoved.then(() => {
            signalrClient.off('CalculationStatusChanged', listener);
          });
        },
      }),
    }),
  });

export const { useGetCalculationCategoryPairingsQuery } = apiCalculationsCategories;
